import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoginPage } from "./pages/LoginPage";
import { SignUpPage } from "./pages/SignUpPage";
import { ConfirmPage } from "./pages/ConfirmPage";
import { PlatformAdminPage } from "./pages/PlatformAdminPage";
import { HomePage } from "./pages/HomePage";
import { TimelinePage } from "./pages/TimelinePage";
import { ListPage } from "./pages/ListPage";
import { KanbanPage } from "./pages/KanbanPage";
import { CalendarPage } from "./pages/CalendarPage";
import { BatchUpdatePage } from "./pages/BatchUpdatePage";
import { ScoringLogPage } from "./pages/ScoringLogPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { CommentsLogPage } from "./pages/CommentsLogPage";
import { DependencyLogPage } from "./pages/DependencyLogPage";
import { HelpPage } from "./pages/HelpPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { IntelligencePage } from "./pages/IntelligencePage";
import { InitiativeFullPage } from "./pages/InitiativeFullPage";
import { InitiativeModal, InitiativeDetailModal } from "./components/modals";
import { SessionExpiredModal } from "./components/SessionExpiredModal";
import { useAppStore } from "./stores/appStore";
import { useAuth } from "./contexts/AuthContext";
import { initiativeApi, fieldConfigApi, labsApi, isMasterHost, isAppsHost, setAuthToken } from "./services/api";
import { MasterHomePage } from "./pages/MasterHomePage";
import { useTour } from "./components/tour/useTour";
import { TourContext } from "./components/tour/TourContext";
import { TourOverlay } from "./components/tour/TourOverlay";

function AppContent() {
  const {
    initiativeModalOpen,
    initiativeModalMode,
    selectedInitiative,
    closeInitiativeModal,
    openEditInitiativeModal,
    openViewInitiativeModal,
    triggerRefresh,
    setFieldConfigs,
    setLabsFeatures,
  } = useAppStore();
  const { isAuthenticated, user } = useAuth();
  const tourHook = useTour();

  // Load field configs and labs features once on authentication so all pages
  // have the correct labels and feature flags without waiting for per-page loads
  useEffect(() => {
    if (!isAuthenticated) return;
    fieldConfigApi.getAll().then((configs) => {
      if (configs.length > 0) setFieldConfigs(configs);
    }).catch(() => {});
    labsApi.getAll().then((labs) => {
      if (labs.length > 0) setLabsFeatures(labs);
    }).catch(() => {});
  }, [isAuthenticated]);
  // Auto-start tour for first-time users
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (localStorage.getItem(`tour_completed_${user.id}`)) return;
    const timer = setTimeout(() => tourHook.startTour(user.role), 800);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const location = useLocation();
  const navigate = useNavigate();

  // Handle /initiative/:id route - auto-open the initiative modal (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;
    const match = location.pathname.match(/^\/initiative\/(.+)$/);
    if (match) {
      const id = match[1];
      initiativeApi.getById(id).then((init) => {
        if (init) openViewInitiativeModal(init);
      });
    }
  }, [location.pathname, isAuthenticated]);

  const handleCloseModal = () => {
    closeInitiativeModal();
    // If on /initiative/:id, navigate back to /home
    if (location.pathname.startsWith('/initiative/')) {
      navigate('/home');
    }
  };

  const handleModalSuccess = () => {
    triggerRefresh();
    handleCloseModal();
  };

  const handleEditFromView = () => {
    if (selectedInitiative) {
      openEditInitiativeModal(selectedInitiative);
    }
  };

  return (
    <TourContext.Provider value={{ startTour: (role: string) => tourHook.startTour(role) }}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/confirm" element={<ConfirmPage />} />
        <Route path="/platform-admin" element={<PlatformAdminPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Navigate to="/home" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/timeline"
          element={
            <ProtectedRoute>
              <TimelinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/list"
          element={
            <ProtectedRoute>
              <ListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kanban"
          element={
            <ProtectedRoute>
              <KanbanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <CalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/batch-update"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <BatchUpdatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scoring"
          element={
            <ProtectedRoute>
              <ScoringLogPage mode="list" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scoring/chart"
          element={
            <ProtectedRoute>
              <ScoringLogPage mode="chart" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/auditlog"
          element={
            <ProtectedRoute>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/commentslog"
          element={
            <ProtectedRoute>
              <CommentsLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dependencies"
          element={
            <ProtectedRoute>
              <DependencyLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/intelligence"
          element={
            <ProtectedRoute>
              <IntelligencePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/initiative/:id"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/initiatives/:id"
          element={
            <ProtectedRoute>
              <InitiativeFullPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>

      {/* Global Initiative View Modal */}
      <InitiativeDetailModal
        isOpen={initiativeModalOpen && initiativeModalMode === 'view'}
        onClose={handleCloseModal}
        initiative={selectedInitiative}
        onEdit={handleEditFromView}
      />

      {/* Global Initiative Add/Edit Modal */}
      <InitiativeModal
        isOpen={initiativeModalOpen && (initiativeModalMode === 'add' || initiativeModalMode === 'edit')}
        onClose={handleCloseModal}
        initiative={initiativeModalMode === 'edit' ? selectedInitiative : null}
        onSuccess={handleModalSuccess}
      />

      {tourHook.isActive && (
        <TourOverlay {...tourHook} userId={user?.id} />
      )}

      {/* Global session-expired prompt (modal + redirect to login) */}
      <SessionExpiredModal />
    </TourContext.Provider>
  );
}

function App() {
  const hash = window.location.hash;
  if (hash.startsWith('#auth_token=')) {
    const incomingToken = hash.slice('#auth_token='.length);
    if (incomingToken) {
      setAuthToken(incomingToken);
      window.history.replaceState({}, '', window.location.pathname + window.location.search);
    }
  }

  // On apps.tabillo.com, serve only public routes — login, signup, confirm
  if (isAppsHost()) {
    return (
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/confirm" element={<ConfirmPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    );
  }

  // On master.tabillo.com, serve the master home page, admin dashboard, or tenant app
  if (isMasterHost()) {
    return (
      <BrowserRouter>
        <Routes>
          {/* Root: master home page with app tiles */}
          <Route path="/" element={<MasterHomePage />} />
          {/* Admin tile: platform admin dashboard (mock data for now, Phase 7 to wire up) */}
          <Route path="/admin" element={<PlatformAdminPage />} />
          <Route path="/admin/*" element={<PlatformAdminPage />} />
{/* All other paths (login, home, timeline, etc.): full tenant app using 'master' slug */}
          <Route
            path="*"
            element={
              <AuthProvider>
                <AppContent />
              </AuthProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
